import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as componentStore from '../component-store'
import * as fields from '../fields'

/**
 * Displays a details component with or without sections
 */
@ngCore.Component({
  imports: [fields.Component, ds.layout.section.Component],
  selector: 'widget-details',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link componentStore.Widget} */
  private readonly _widgetStore = ngCore.inject(
    componentStore.Widget<ents.core.mosaic.Widget.Type.Details>,
  )

  /** @see {@link ents.core.display.Field.Collection.WithSections} */
  public readonly sectionsWithFieldsSg = ngCore.computed(
    (sections = this._widgetStore.contentSg()?.sections) => sections,
  )

  /**
   * Fields. Empty array if details are to be shown with sections
   */
  public readonly fieldsSg = ngCore.computed(
    (widgetFields = this._widgetStore.contentSg()?.fields) =>
      widgetFields ?? [],
  )

  /** @see {@link ents.core.display.Field.Group.columnCount}*/
  private readonly _desiredFieldsColumnCountSg = ngCore.computed(
    (content = this._widgetStore.contentSg()) => content?.columnCount,
  )

  /**
   * Maps each section to its corresponding column count for field rendering
   *
   * @example
   * // Array where each index corresponds to a section's column count
   * // e.g., [1, 2, 2] means:
   * // - section 0: 1 column
   * // - section 1: 2 columns
   * // - section 2: 2 columns
   */
  public readonly sectionWithFieldsColumnCountLookupSg = ngCore.computed(
    (sections = this.sectionsWithFieldsSg() ?? []) =>
      sections.map(
        section =>
          section.columnCount ??
          _getDefaultColumnCountForFields(section.fields ?? []),
      ),
  )

  /**
   * Number of columns to use when rendering fields
   *
   * @see {@link _getDefaultColumnCountForFields}
   */
  public readonly fieldsColumnCountSg = ngCore.computed(
    (
      _fields = this.fieldsSg(),
      desiredFieldsColumnCountSg = this._desiredFieldsColumnCountSg(),
    ) => desiredFieldsColumnCountSg ?? _getDefaultColumnCountForFields(_fields),
  )
}

/**
 * Determines the number of columns to use based on
 * {@link ents.core.display.Field fields} configuration.
 *
 * __NOTE:__ By default the number of columns will be determined by the number
 * of input fields. If there are more input fields than
 * {{@link ents.core.display.Field.readonly readonly}} fields, a single column
 * layout will be used, otherwise a two column layout will be used.
 *
 */
function _getDefaultColumnCountForFields(
  _fields: readonly ents.core.display.Field[],
): number {
  const inputFields = _fields.filter(field => !field.readonly)
  return inputFields.length >= _fields.length / 2 ? 1 : 2
}
